class MegaMenu extends HTMLElement {
    constructor() {
        super();
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onCollectionLinkClick = this.onCollectionLinkClick.bind(this);
        this.onItemMouseEnter = this.onItemMouseEnter.bind(this);
        this.onMegaMenuMouseLeave = this.onMegaMenuMouseLeave.bind(this);
    }

    connectedCallback() {
        this.collectionItems = this.querySelectorAll(".mega-menu__collection-item--has-submenu");
        this.hasHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

        this.collectionItems.forEach((item) => {
            const link = item.querySelector("a");
            link.addEventListener("click", this.onCollectionLinkClick);

            if (this.hasHover) {
                item.addEventListener("mouseenter", this.onItemMouseEnter);
            }
        });

        if (this.hasHover) {
            this.addEventListener("mouseleave", this.onMegaMenuMouseLeave);
        }

        document.addEventListener("click", this.onDocumentClick);
        this.initSticky();
    }

    disconnectedCallback() {
        document.removeEventListener("click", this.onDocumentClick);
        if (this._onScroll) window.removeEventListener("scroll", this._onScroll);
        if (this._onResize) window.removeEventListener("resize", this._onResize);
        if (this._placeholder) this._placeholder.remove();
    }

    /**
     * Opens a submenu on hover for pointer devices.
     * @param {Event} event The mouseenter event.
     * @returns {void}
     */
    onItemMouseEnter(event) {
        const item = event.currentTarget;
        this.closeAllItems();
        this.openItem(item);
    }

    /**
     * Closes open submenus when the pointer leaves the mega menu.
     * @returns {void}
     */
    onMegaMenuMouseLeave() {
        this.closeAllItems();
    }

    /**
     * Handles the click event on a collection link.
     * @param {Event} event The click event.
     * @returns {void}
     */
    onCollectionLinkClick(event) {
        const item = event.currentTarget.closest(".mega-menu__collection-item");

        if (item.classList.contains("is-open")) return;

        event.preventDefault();
        event.stopPropagation();
        this.closeAllItems();
        this.openItem(item);
    }

    /**
     * Handles the click event on the document.
     * @param {Event} event The click event.
     * @returns {void}
     */
    onDocumentClick(event) {
        if (!this.contains(event.target)) {
            this.closeAllItems();
        }
    }

    /**
     * Returns the submenu panel for a collection item.
     * @param {Element} item The collection item.
     * @returns {Element|null}
     */
    getSubmenuForItem(item) {
        const menuId = item.dataset.menuId;
        if (!menuId) return null;

        return this.querySelector(`.mega-menu__sub-collections[data-menu-id="${menuId}"]`);
    }

    /**
     * Opens an item.
     * @param {Element} item The item to open.
     * @returns {void}
     */
    openItem(item) {
        const submenu = this.getSubmenuForItem(item);
        if (!submenu) return;

        item.classList.add("is-open");
        submenu.classList.add("is-open");

        const link = item.querySelector("a");
        if (link) link.setAttribute("aria-expanded", "true");

        this.classList.add("mega-menu--active");
    }

    /**
     * Closes all items.
     * @returns {void}
     */
    closeAllItems() {
        this.querySelectorAll(".mega-menu__collection-item.is-open").forEach((item) => {
            item.classList.remove("is-open");
            const link = item.querySelector("a");
            if (link) link.setAttribute("aria-expanded", "false");
        });

        this.querySelectorAll(".mega-menu__sub-collections.is-open").forEach((submenu) => {
            submenu.classList.remove("is-open");
        });

        this.classList.remove("mega-menu--active");
    }

    /**
     * Sets up sticky behaviour: fixes the menu to the top once the user
     * scrolls past its natural position, and uses a placeholder element
     * to prevent the layout from collapsing.
     * @returns {void}
     */
    initSticky() {
        const menuTop = this.getBoundingClientRect().top + window.scrollY;

        this._placeholder = document.createElement("div");
        this._placeholder.style.cssText = `height:${this.offsetHeight}px;display:none;`;
        this.after(this._placeholder);

        this._isSticky = false;
        let lastScrollY = window.scrollY;

        // Height taken up by the sticky announcement bar, if it is enabled.
        // The menu docks underneath it instead of behind it.
        const readStickyOffset = () =>
            parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--announcement-bar-height")) || 0;

        this._stickyOffset = readStickyOffset();
        this._onResize = () => {
            this._stickyOffset = readStickyOffset();
        };
        window.addEventListener("resize", this._onResize, { passive: true });

        this._onScroll = () => {
            const scrollY = window.scrollY;
            const scrollingUp = scrollY < lastScrollY;
            lastScrollY = scrollY;

            const pastThreshold = scrollY > menuTop - this._stickyOffset;

            // Scrolled back above the natural position — revert to normal
            if (!pastThreshold) {
                if (this._isSticky) {
                    this._isSticky = false;
                    this.classList.remove("mega-menu--sticky");
                    this._placeholder.style.display = "none";
                }
                return;
            }

            // Past threshold: show on scroll up, hide on scroll down
            const shouldBeSticky = scrollingUp;
            if (shouldBeSticky === this._isSticky) return;
            this._isSticky = shouldBeSticky;

            if (shouldBeSticky) {
                this.classList.add("mega-menu--sticky", "mega-menu--sticky-entering");
                this._placeholder.style.display = "block";
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => this.classList.remove("mega-menu--sticky-entering"));
                });
            } else {
                this.classList.remove("mega-menu--sticky", "mega-menu--sticky-entering");
                this._placeholder.style.display = "none";
            }
        };

        window.addEventListener("scroll", this._onScroll, { passive: true });
        this._onScroll();
    }
}

customElements.define("mega-menu", MegaMenu);
